// FieldIQ — Shared UI primitives: Avatar, Badge, FormatMsg, SourceCitations

import { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, FileText } from "lucide-react";
import { parseMessageText } from "../lib/helpers";

export const Avatar = ({
  name,
  photoUrl,
  size = 10,
  colorClass = "from-cyan-500 to-blue-600",
}) => {
  const [imgError, setImgError] = useState(false);
  useEffect(() => {
    setImgError(false);
  }, [photoUrl]);

  const initials = name
    ? name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "?";

  if (photoUrl && !imgError) {
    return (
      <img
        src={photoUrl}
        alt={name || "avatar"}
        className={`w-${size} h-${size} rounded-full object-cover flex-shrink-0`}
        onError={() => setImgError(true)}
      />
    );
  }
  return (
    <div
      className={`w-${size} h-${size} rounded-full bg-gradient-to-br ${colorClass} flex items-center justify-center text-white font-semibold text-sm flex-shrink-0`}
    >
      {initials}
    </div>
  );
};

export const Badge = ({ label, color }) => {
  const colors = {
    green: "bg-green-500/15 text-green-400 border-green-500/30",
    amber: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    cyan: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30",
    purple: "bg-purple-500/15 text-purple-400 border-purple-500/30",
    slate: "bg-slate-700 text-slate-400 border-slate-600",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${colors[color] ?? colors.slate}`}
    >
      {label}
    </span>
  );
};

// ═══════════════════════════════════════════════════════════════════
// CHAT HELPERS
// ═══════════════════════════════════════════════════════════════════

export const FormatMsg = ({ text, accentColor }) => {
  const lines = text.split("\n");
  return lines.map((line, li) => (
    <span key={li}>
      {li > 0 && <br />}
      {parseMessageText(line).map((seg) =>
        seg.type === "link" ? (
          <a
            key={seg.key}
            href={seg.value}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: accentColor || "#6b8fc4",
              textDecoration: "underline",
            }}
          >
            {seg.value}
          </a>
        ) : (
          <span key={seg.key}>{seg.value}</span>
        ),
      )}
    </span>
  ));
};

export const SourceCitations = ({ sources, colors, borderRadius }) => {
  const [expanded, setExpanded] = useState(false);
  if (!sources || sources.length === 0) return null;
  const br = Math.max(4, (borderRadius || 12) - 4);
  return (
    <div className="mt-2">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-xs font-medium transition-colors hover:opacity-80"
        style={{ color: colors.accent }}
      >
        <FileText className="w-3 h-3" />
        {sources.length} source{sources.length !== 1 ? "s" : ""}
        {expanded ? (
          <ChevronUp className="w-3 h-3" />
        ) : (
          <ChevronDown className="w-3 h-3" />
        )}
      </button>
      {expanded && (
        <div className="mt-2 space-y-1.5">
          {sources.map((src, i) => (
            <div
              key={i}
              className="px-3 py-2 text-xs"
              style={{
                backgroundColor: `${colors.accent}12`,
                border: `1px solid ${colors.accent}20`,
                borderRadius: `${br}px`,
                color: colors.text,
              }}
            >
              <div className="flex items-center justify-between mb-1">
                <span
                  className="font-medium"
                  style={{ color: colors.headings }}
                >
                  {src.url ? (
                    <a
                      href={src.url}
                      target="_blank"
                      rel="noreferrer"
                      className="underline underline-offset-2 hover:opacity-70"
                      style={{ color: colors.accent }}
                    >
                      {src.name || src.source || `Document ${i + 1}`}
                    </a>
                  ) : (
                    src.name || src.source || `Document ${i + 1}`
                  )}
                </span>
                {src.score != null && (
                  <span
                    className="px-1.5 py-0.5 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor:
                        src.score > 0.8
                          ? "#22c55e20"
                          : src.score > 0.6
                            ? "#f59e0b20"
                            : "#ef444420",
                      color:
                        src.score > 0.8
                          ? "#22c55e"
                          : src.score > 0.6
                            ? "#f59e0b"
                            : "#ef4444",
                    }}
                  >
                    {(src.score * 100).toFixed(0)}% match
                  </span>
                )}
              </div>
              {src.snippet && (
                <p className="leading-relaxed mt-1" style={{ opacity: 0.7 }}>
                  {src.snippet.length > 200
                    ? src.snippet.slice(0, 200) + "…"
                    : src.snippet}
                </p>
              )}
              {src.verified_at && (
                <p className="mt-1 text-[10px]" style={{ opacity: 0.5 }}>
                  Verified{" "}
                  {Math.floor(
                    (Date.now() - new Date(src.verified_at).getTime()) /
                      86400000,
                  )}
                  d ago
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const parseSources = (data) => {
  const raw =
    data.sourceDocuments ||
    data.sources ||
    data.documents ||
    data.context ||
    [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((doc) => ({
      name:
        doc.metadata?.source_name ||
        doc.metadata?.source ||
        doc.metadata?.fileName ||
        doc.name ||
        doc.source ||
        "",
      url: doc.metadata?.source_url || doc.metadata?.url || null,
      verified_at: doc.metadata?.verified_at || null,
      score: doc.score ?? doc.metadata?.score ?? null,
      snippet: doc.pageContent || doc.content || doc.text || doc.snippet || "",
    }))
    .slice(0, 10);
};
